import React, {useRef, useState} from "react";
import {Button, DatePicker, Row, Segmented, Space, Tag} from "antd";
import {CalendarOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import {
    DASHBOARD_DATE_PRESETS,
    getDashboardDateRangeForPreset,
    getDashboardDefaultDateRange,
    getDashboardPresetForRange,
} from "./dashboard-chart-utils";

const {RangePicker} = DatePicker;

const toDayjsRange = (startDate, endDate) => [dayjs(startDate), dayjs(endDate)];

const DashboardChartTitle = ({title, value, tagColor, chartColor, onRangeChange}) => {
    const [open, setOpen] = useState(false);
    const [activePreset, setActivePreset] = useState("14d");
    const [range, setRange] = useState(() => {
        const {startDate, endDate} = getDashboardDefaultDateRange();

        return toDayjsRange(startDate, endDate);
    });
    const triggerRef = useRef(null);

    const applyRange = (startDate, endDate, preset = null) => {
        const dates = toDayjsRange(startDate, endDate);
        const resolvedPreset = preset ?? getDashboardPresetForRange(startDate, endDate);

        setRange(dates);
        setActivePreset(resolvedPreset);
        onRangeChange(dates, [startDate, endDate]);
    };

    const handlePresetChange = (presetKey) => {
        const {startDate, endDate} = getDashboardDateRangeForPreset(presetKey);
        applyRange(startDate, endDate, presetKey);
    };

    const handleChange = (dates, dateStrings) => {
        if (dates?.[0] && dates?.[1]) {
            applyRange(dateStrings[0], dateStrings[1]);
            setOpen(false);
            return;
        }

        const {startDate, endDate} = getDashboardDefaultDateRange();
        applyRange(startDate, endDate, "14d");
    };

    return (
        <div className="dashboard-chart-card__title">
            <Row type="flex" align="middle" justify="space-between">
                <span>{title}</span>
                <Space size={4} align="center">
                    {value != null && value !== false && (
                        <Tag color={tagColor}>{value}</Tag>
                    )}
                    <div className="dashboard-chart-card__date-trigger" ref={triggerRef}>
                        <RangePicker
                            open={open}
                            value={range}
                            onOpenChange={setOpen}
                            onChange={handleChange}
                            allowClear
                            getPopupContainer={() => triggerRef.current || document.body}
                            className="dashboard-chart-card__picker-hidden"
                        />
                        <Button
                            type="text"
                            icon={<CalendarOutlined/>}
                            aria-label="Select date range"
                            onClick={() => setOpen(true)}
                        />
                    </div>
                </Space>
            </Row>

            <Segmented
                className="dashboard-chart-card__presets"
                size="small"
                style={{"--chart-accent-color": chartColor}}
                value={activePreset ?? undefined}
                options={DASHBOARD_DATE_PRESETS.map(({key, label}) => ({
                    value: key,
                    label,
                }))}
                onChange={handlePresetChange}
            />
        </div>
    );
};

export default DashboardChartTitle;
